import { useMemo, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { monitorForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { autoScrollForElements } from '@atlaskit/pragmatic-drag-and-drop-auto-scroll/element';
import { color, EL_COLORS } from '@/colors';
import { useCompositorView, useNodeData, useFlowState } from '@/components/Recipe/FlowContext';
import { I18N_KEYS } from '@/language/keys';
import { FlexCenVer, FlexCol } from '@/UI/styles';
import { PANEL_HEADER_HEIGHT, PANEL_WIDTH } from '@/components/Recipe/consts/ui';
import AppDivider from '@/UI/AppDivider/AppDivider';
import { TextSmMd, TextStdMd } from '@/UI/AppTypography/AppTypography';
import { LayerPanelRow } from './LayerPanelRow';
import { DnDWrapper, LAYER_PANEL_DND_TYPE } from './DnDWrapper';
import type { NodeId } from 'web';
import type { CompositorNodeV3 } from '@/types/nodes/compositor';

interface LayerPanelProps {
  nodeId: NodeId;
}

export function LayerPanel({ nodeId }: LayerPanelProps) {
  const {
    name,
    data: { layers, layerOrder },
  } = useNodeData<CompositorNodeV3>(nodeId);
  const selectedLayer = useFlowState((s) => s.compositor[nodeId]?.selectedLayer);

  const compositorView = useCompositorView(nodeId);
  const { t } = useTranslation();
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    return monitorForElements({
      canMonitor: ({ source }) => source.data.type === LAYER_PANEL_DND_TYPE,
      onDrop({ source, location }) {
        const destination = location.current.dropTargets[0];
        if (destination == null) return;
        const destinationIndex = destination.data.index;
        const sourceDragKey = source.data.dragKey;
        const sourceIndex = source.data.index;

        if (
          typeof destinationIndex !== 'number' ||
          typeof sourceDragKey !== 'number' ||
          typeof sourceIndex !== 'number'
        ) {
          return;
        }
        if (destinationIndex === sourceIndex) return;
        compositorView.setLayerOrder(sourceDragKey, destinationIndex);
      },
    });
  }, [compositorView]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    return autoScrollForElements({
      element: el,
      canScroll: ({ source }) => source.data.type === LAYER_PANEL_DND_TYPE,
    });
  }, []);

  const reversedLayerOrder = useMemo(() => [...layerOrder].reverse(), [layerOrder]);

  return (
    <FlexCol
      data-testid="compositor-layers-panel"
      sx={{
        background: color.Black92,
        borderRight: `1px solid ${EL_COLORS.DividerBorder}`,
        flexShrink: 0,
        width: PANEL_WIDTH,
      }}
    >
      <FlexCenVer sx={{ px: 2, height: PANEL_HEADER_HEIGHT, flexShrink: 0 }}>
        <TextStdMd text={name} />
      </FlexCenVer>
      <AppDivider />

      <FlexCol sx={{ px: 2, pt: 3, gap: 2, minHeight: 0 }}>
        <TextSmMd text={t(I18N_KEYS.COMPOSITOR.LAYER_PANEL.TITLE)} />
        {/* minHeight is needed for inner scroll */}
        <FlexCol ref={scrollRef} sx={{ overflowY: 'auto', minHeight: 0 }} className="wea-no-scrollbar">
          {/* canvas row */}
          <LayerPanelRow isSelected={selectedLayer === undefined} nodeId={nodeId} />
          {reversedLayerOrder.map((layerId, reversedIndex) =>
            layers[layerId] ? (
              <DnDWrapper key={layerId} dragKey={layerId} index={reversedLayerOrder.length - reversedIndex - 1}>
                <LayerPanelRow
                  isSelected={selectedLayer === layerId}
                  layer={layers[layerId]}
                  layerId={layerId}
                  nodeId={nodeId}
                />
              </DnDWrapper>
            ) : null,
          )}
        </FlexCol>
      </FlexCol>
    </FlexCol>
  );
}
